const boxSizing = {
    name: "box-sizing",
    path: "/box-sizing",
    title: "box-sizing",
    description: "Utilities for controlling how the browser should calculate an element's total size.",
    value: {
        "box-sizing": {
          "box-border": "border-box",
        "box-content": "content-box",
        }
    },
    exampleData: [
        {
            "title": "",
            "description": "",
            "example": {
                "element": "",
                "src": "",
                "className": "",
                "code": `<div class="box-border size-32 border-4 p-4 ...">
  <!-- ... -->
</div>`
            }
        },
            {
            "title": "",
            "description": "",
            "example": {
                "element": "",
                "src": "",
                "className": "",
                "code": `<div class="box-border size-32 border-4 p-4 ...">
  <!-- ... -->
</div>`
            }
        }
    ],
    htmlData: [
        {
            data: `
            <div class="">
              <p class="text-color-gray-600 text-sm dark:text-color-gray-300"><code class="text-color-gray-800 dark:text-color-white font-semibold">box-border</code> utility to set an element's box-sizing to border-box, telling the browser to include the element's borders and padding when you give it a height or width.</p>
              <p class="mt-4 text-color-gray-600 text-sm dark:text-color-gray-300">This means a 100px × 100px element with a 2px border and 4px of padding on all sides will be rendered as 100px × 100px, with an internal content area of 88px × 88px:</p>
              <div class="mt-10 relative grid w-full grid-columns-[1fr_8rem_1fr] grid-template-rows-[1fr_3fr_1fr] gap-px bg-color-gray-700/10 font-mono text-sm leading-6 font-bold dark:bg-color-gray-700">
                <div class="column-start-1 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-2 row-start-1 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute right-0 bottom-2 left-0 flex">
                    <div class="absolute top-1/2 right-0 left-0 h-px -translate-y-1px bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-1/2 left-0 h-2 w-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex w-full flex-auto justify-items-center justify-center bg-color-white px-1.5px font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute top-1/2 right-0 h-2 w-px translate-x-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-1 row-start-2 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute top-0 right-2 bottom-0 flex w-3">
                    <div class="absolute top-0 bottom-0 left-1/2 w-px -translate-x-[0.5px] bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-0 left-1/2 h-px w-2 -translate-x-1 -translate-y-1px rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex h-3 flex-auto translate-x-[-12px] translate-y-14 -rotate-90 justify-items-center justify-center bg-color-white px-1.5 font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute bottom-0 left-1/2 h-px w-2 -translate-x-1 translate-y-px rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-2 row-start-2 size-32 bg-color-white ring-1 ring-color-sky-300 dark:bg-color-gray-900 dark:ring-color-sky-400">
                  <div class="relative box-border size-32 p-5 ring ring-inset-color-sky-300 ring-inset">
                    <div class="relative z-10 h-full w-full bg-color-sky-500 ring-1 ring-color-sky-500"></div>
                    <div class="absolute inset-0">
                      <div class="h-full text-color-black/10 dark:text-color-white/12.5 bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)]"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-2 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-1 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-2 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-3 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
              </div>
            </div>

            `
        },
         {
            data: `
            <div class="">
              <p class="text-color-gray-600 text-sm dark:text-color-gray-300"><code class="text-color-gray-800 dark:text-color-white font-semibold">box-content</code> utility to set an element's box-sizing to content-box, telling the browser to add borders and padding on top of the element's specified width or height.</p>
              <p class="mt-4 text-color-gray-600 text-sm dark:text-color-gray-300">This means a 100px × 100px element with a 2px border and 4px of padding on all sides will actually be rendered as 112px × 112px, with an internal content area of 100px × 100px:</p>
              <div class="mt-10 relative grid w-full grid-columns-[1fr_8rem_1fr] grid-template-rows-[1fr_3fr_1fr] gap-px bg-color-gray-700/10 font-mono text-sm leading-6 font-bold dark:bg-color-gray-700">
                <div class="column-start-1 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-2 row-start-1 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute right-0 bottom-6 left-0 flex">
                    <div class="absolute top-1/2 right-0 left-0 h-px -translate-y-1px bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-1/2 left-0 h-2 w-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex w-full flex-auto justify-items-center justify-center bg-color-white px-1.5px font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute top-1/2 right-0 h-2 w-px translate-x-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-1 row-start-2 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute top-0 right-6 bottom-0 flex w-3">
                    <div class="absolute top-0 bottom-0 left-1/2 w-px -translate-x-[0.5px] bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-0 left-1/2 h-px w-2 -translate-x-1 -translate-y-1px rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex h-3 flex-auto translate-x-[-12px] translate-y-14 -rotate-90 justify-items-center justify-center bg-color-white px-1.5 font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute bottom-0 left-1/2 h-px w-2 -translate-x-1 translate-y-px rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-2 row-start-2 size-32 bg-color-white ring-1 ring-color-sky-300 dark:bg-color-gray-900 dark:ring-color-sky-400">
                  <div class="relative box-content size-32 -translate-x-5 -translate-y-5 p-5 ring-3px ring-color-sky-300 ring-inset">
                    <div class="relative z-10 h-full w-full bg-color-sky-500 ring-1 ring-color-sky-500"></div>
                    <div class="absolute inset-0 z-[0]">
                      <div class="h-full text-color-black/10 dark:text-color-white/12.5 bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)]"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-2 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-1 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-2 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-3 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
              </div>
            </div>
            `
        }
    ]
};

export default boxSizing;


<div className="sr-only">
  <div class="">
              <p class="text-color-gray-600 text-sm dark:text-color-gray-300"><code class="text-color-gray-800 dark:text-color-white font-semibold">box-border</code> utility to set an element's box-sizing to border-box, telling the browser to include the element's borders and padding when you give it a height or width.</p>
              <p class="mt-4 text-color-gray-600 text-sm dark:text-color-gray-300">This means a 100px × 100px element with a 2px border and 4px of padding on all sides will be rendered as 100px × 100px, with an internal content area of 88px × 88px:</p>
              <div class="mt-10 relative grid w-full grid-columns-[1fr_8rem_1fr] grid-template-rows-[1fr_3fr_1fr] gap-px bg-color-gray-700/10 font-mono text-sm leading-6 font-bold dark:bg-color-gray-700">
                <div class="column-start-1 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-2 row-start-1 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute right-0 bottom-2 left-0 flex">
                    <div class="absolute top-1/2 right-0 left-0 h-px -translate-y-1px bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-1/2 left-0 h-2 w-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex w-full flex-auto justify-items-center justify-center bg-color-white px-1.5px font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute top-1/2 right-0 h-2 w-px translate-x-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-1 row-start-2 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute top-0 right-2 bottom-0 flex w-3">
                    <div class="absolute top-0 bottom-0 left-1/2 w-px -translate-x-[0.5px] bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-0 left-1/2 h-px w-2 -translate-x-1 -translate-y-1px rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex h-3 flex-auto translate-x-[-12px] translate-y-14 -rotate-90 justify-items-center justify-center bg-color-white px-1.5 font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute bottom-0 left-1/2 h-px w-2 -translate-x-1 translate-y-px rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-2 row-start-2 size-32 bg-color-white ring-1 ring-color-sky-300 dark:bg-color-gray-900 dark:ring-color-sky-400">
                  <div class="relative box-border size-32 p-5 ring ring-inset-color-sky-300 ring-inset">
                    <div class="relative z-10 h-full w-full bg-color-sky-500 ring-1 ring-color-sky-500"></div>
                    <div class="absolute inset-0">
                      <div class="h-full text-color-black/10 dark:text-color-white/12.5 bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)]"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-2 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-1 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-2 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-3 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
              </div>
            </div>
            <div class="">
              <p class="text-color-gray-600 text-sm dark:text-color-gray-300"><code class="text-color-gray-800 dark:text-color-white font-semibold">box-content</code> utility to set an element's box-sizing to content-box, telling the browser to add borders and padding on top of the element's specified width or height.</p>
              <p class="mt-4 text-color-gray-600 text-sm dark:text-color-gray-300">This means a 100px × 100px element with a 2px border and 4px of padding on all sides will actually be rendered as 112px × 112px, with an internal content area of 100px × 100px:</p>
              <div class="mt-10 relative grid w-full grid-columns-[1fr_8rem_1fr] grid-template-rows-[1fr_3fr_1fr] gap-px bg-color-gray-700/10 font-mono text-sm leading-6 font-bold dark:bg-color-gray-700">
                <div class="column-start-1 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-2 row-start-1 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute right-0 bottom-6 left-0 flex">
                    <div class="absolute top-1/2 right-0 left-0 h-px -translate-y-1px bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-1/2 left-0 h-2 w-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex w-full flex-auto justify-items-center justify-center bg-color-white px-1.5px font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute top-1/2 right-0 h-2 w-px translate-x-px -translate-y-1 rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-1 bg-color-white dark:bg-color-gray-900"></div>
                <div class="relative column-start-1 row-start-2 bg-color-white dark:bg-color-gray-900">
                  <div class="absolute top-0 right-6 bottom-0 flex w-3">
                    <div class="absolute top-0 bottom-0 left-1/2 w-px -translate-x-[0.5px] bg-color-sky-400"></div>
                    <div class="w-full">
                      <div class="absolute top-0 left-1/2 h-px w-2 -translate-x-1 -translate-y-1px rounded-full bg-color-sky-400"></div>
                    </div>
                    <div class="relative flex h-3 flex-auto -translate-x-[-12px] translate-y-14 -rotate-90 justify-items-center justify-center bg-color-white px-1.5 font-mono text-xs leading-none font-bold text-color-sky-600 dark:bg-color-gray-900 dark:text-color-sky-400">128px</div>
                    <div class="w-full">
                      <div class="absolute bottom-0 left-1/2 h-px w-2 -translate-x-1 translate-y-px rounded-full bg-color-sky-400"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-2 row-start-2 size-32 bg-color-white ring-1 ring-color-sky-300 dark:bg-color-gray-900 dark:ring-color-sky-400">
                  <div class="relative box-content size-32 -translate-x-5 -translate-y-5 p-5 ring-3px ring-color-sky-300 ring-inset">
                    <div class="relative z-10 h-full w-full bg-color-sky-500 ring-1 ring-color-sky-500"></div>
                    <div class="absolute inset-0 z-[0]">
                      <div class="h-full text-color-black/10 dark:text-color-white/12.5 bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)]"></div>
                    </div>
                  </div>
                </div>
                <div class="column-start-3 row-start-2 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-1 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-2 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
                <div class="column-start-3 row-start-3 bg-color-white dark:bg-color-gray-900"></div>
              </div>
            </div>
</div>